import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import type { Item, StockMovementKind } from '@iobiz/shared'
import { recordMovement } from './api.js'

/**
 * Saisie groupée de mouvements (réception, inventaire).
 *
 * ⭐ C'est le geste RÉEL du métier : quand une livraison arrive, on ne met pas
 * à jour dix articles en ouvrant dix fiches, on saisit un bon de livraison.
 * Le motif et la référence sont donc communs à tout le lot, seules les
 * quantités changent.
 *
 * ⚠️ Chaque ligne part en mouvement INDÉPENDANT. Un lot atomique serait pire :
 * une seule ligne en erreur annulerait une réception entière déjà rangée en
 * rayon. On applique ce qui passe et on montre précisément ce qui a échoué.
 */

interface Ligne {
  item: Item | null
  quantite: string
}

export function StockBatchDialog({
  open,
  storeId,
  items,
  onClose,
  onDone,
}: {
  open: boolean
  storeId: string
  items: Item[]
  onClose: () => void
  onDone: () => void
}): JSX.Element {
  const [kind, setKind] = useState<StockMovementKind>('in')
  const [motif, setMotif] = useState('Réception fournisseur')
  const [reference, setReference] = useState('')
  const [lignes, setLignes] = useState<Ligne[]>([{ item: null, quantite: '' }])
  const [envoi, setEnvoi] = useState(false)
  const [progression, setProgression] = useState(0)
  const [echecs, setEchecs] = useState<{ nom: string; raison: string }[]>([])
  const [succes, setSucces] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setLignes([{ item: null, quantite: '' }])
    setEchecs([])
    setSucces(null)
    setProgression(0)
  }, [open])

  // Un article non suivi n'a pas de stock à mouvementer : on ne le propose pas.
  const suivis = items.filter((i) => i.trackStock)

  const pretes = lignes.filter((l) => l.item && Number(l.quantite) !== 0)

  function majLigne(index: number, patch: Partial<Ligne>): void {
    setLignes((ls) => {
      const suite = ls.map((l, i) => (i === index ? { ...l, ...patch } : l))
      // Une ligne vide reste toujours disponible en bas : saisir dix articles
      // ne doit pas demander dix clics sur « ajouter ».
      const derniere = suite[suite.length - 1]
      if (derniere?.item || derniere?.quantite) suite.push({ item: null, quantite: '' })
      return suite
    })
  }

  async function appliquer(): Promise<void> {
    setEnvoi(true)
    setEchecs([])
    setSucces(null)
    const rates: { nom: string; raison: string }[] = []
    let ok = 0

    for (const [index, ligne] of pretes.entries()) {
      try {
        await recordMovement(storeId, ligne.item!.id, {
          itemId: ligne.item!.id,
          kind,
          quantity: Number(ligne.quantite),
          reason: motif || undefined,
          reference: reference || undefined,
        })
        ok += 1
      } catch (e: unknown) {
        rates.push({
          nom: ligne.item!.name,
          raison: e instanceof Error ? e.message : 'Refusé par le serveur',
        })
      }
      setProgression(((index + 1) / pretes.length) * 100)
    }

    setEnvoi(false)
    setSucces(ok)
    setEchecs(rates)
    if (rates.length === 0) {
      onDone()
      onClose()
    } else {
      // On garde la boîte ouverte : l'utilisateur doit voir ce qui n'est pas
      // passé, et pouvoir le reprendre.
      onDone()
    }
  }

  return (
    <Dialog open={open} onClose={envoi ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Saisie groupée de stock</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start', mb: 4 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={kind}
            onChange={(_, v: StockMovementKind | null) => v && setKind(v)}
          >
            <ToggleButton value="in">Entrée</ToggleButton>
            <ToggleButton value="out">Sortie</ToggleButton>
            <ToggleButton value="adjustment">Ajustement</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            label="Motif"
            size="small"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {(kind === 'in'
              ? ['Réception fournisseur', 'Retour client', 'Transfert entrant']
              : kind === 'out'
                ? ['Vente', 'Casse ou perte', 'Transfert sortant']
                : ['Inventaire', 'Correction de saisie']
            ).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Référence"
            size="small"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helperText="Commune à tout le lot"
            sx={{ minWidth: 220 }}
          />
        </Box>

        {lignes.map((ligne, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
            <Autocomplete
              options={suivis}
              getOptionLabel={(o) => `${o.name}${o.sku ? ` · ${o.sku}` : ''}`}
              value={ligne.item}
              onChange={(_, v) => majLigne(index, { item: v })}
              sx={{ flex: 1, minWidth: 0 }}
              renderInput={(params) => <TextField {...params} label="Article" size="small" />}
            />
            <TextField
              label="Quantité"
              type="number"
              size="small"
              value={ligne.quantite}
              onChange={(e) => majLigne(index, { quantite: e.target.value })}
              sx={{ width: 130 }}
            />
            {lignes.length > 1 && (
              <IconButton
                size="small"
                aria-label="Retirer cette ligne"
                onClick={() => setLignes((ls) => ls.filter((_, i) => i !== index))}
                sx={{ mt: 0.5 }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        ))}

        {envoi && <LinearProgress variant="determinate" value={progression} sx={{ mt: 3 }} />}

        {succes !== null && (
          <Alert severity={echecs.length === 0 ? 'success' : 'warning'} sx={{ mt: 3 }}>
            {succes} mouvement{succes > 1 ? 's' : ''} enregistré{succes > 1 ? 's' : ''}.
            {echecs.length > 0 && ` ${echecs.length} en échec.`}
          </Alert>
        )}

        {echecs.map((e) => (
          <Typography key={e.nom} variant="caption" sx={{ display: 'block', color: 'error.main' }}>
            {e.nom} — {e.raison}
          </Typography>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose} disabled={envoi}>
          Fermer
        </Button>
        <Button
          variant="contained"
          onClick={() => void appliquer()}
          disabled={envoi || pretes.length === 0}
        >
          {envoi
            ? 'Enregistrement…'
            : `Enregistrer ${pretes.length} mouvement${pretes.length > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
